import React from 'react'

import {
  useJobId,
  usePipelineExistingJob,
} from '../../../../../../../../EditPipelinePage/EditPipelinePage.hooks'
import {JobStepBooleanProperty} from '../JobStepBooleanProperty/JobStepBooleanProperty'
import {JobStepEnumProperty} from '../JobStepEnumProperty/JobStepEnumProperty'
import type {HotProperty} from '../JobStepHotFields/JobStepHotFields.descriptors'
import type {JobStepHotFieldsProps} from '../JobStepHotFields/JobStepHotFields.types'
import JobStepStringProperty from '../JobStepStringProperty/JobStepStringProperty'

type Props = HotProperty & JobStepHotFieldsProps

export function JobStepProperty({
  step,
  stepIndex,
  withAdvanced,
  onStepPropertyChange,
  onStepPropertiesChange,
  ...hotProperty
}: Props) {
  const jobId = useJobId()!
  const existing = usePipelineExistingJob(jobId)
  const savedStep = existing?.steps?.[stepIndex]

  const value = step[hotProperty.name]
  const savedValue = savedStep?.[hotProperty.name]
  const isAutoFocus = hotProperty.autoFocus

  if (hotProperty.advanced && !withAdvanced) {
    return null
  }

  if (hotProperty.boolean) {
    return (
      <JobStepBooleanProperty
        key={hotProperty.name}
        name={hotProperty.name}
        value={value}
        stepIndex={stepIndex}
        onChange={onStepPropertyChange}
        label={hotProperty.label}
        updated={existing != null && value !== savedValue}
        description={hotProperty.description}
        trueValue={hotProperty.trueValue}
        falseValue={hotProperty.falseValue}
      />
    )
  }

  if (hotProperty.enum) {
    return (
      <JobStepEnumProperty
        key={hotProperty.name}
        name={hotProperty.name}
        value={value}
        stepIndex={stepIndex}
        onChange={onStepPropertyChange}
        label={hotProperty.label}
        updated={existing != null && value !== savedValue}
        description={hotProperty.description}
        options={hotProperty.options}
      >
        {params =>
          params?.map(param => (
            <JobStepProperty
              key={param.name}
              {...param}
              step={step}
              stepIndex={stepIndex}
              onStepPropertiesChange={onStepPropertiesChange}
              onStepPropertyChange={onStepPropertyChange}
              withAdvanced
            />
          ))
        }
      </JobStepEnumProperty>
    )
  }

  return (
    <JobStepStringProperty
      key={hotProperty.name}
      stepIndex={stepIndex}
      name={hotProperty.name}
      label={hotProperty.label}
      withAdvanced={withAdvanced}
      value={value}
      type={step.type}
      onChange={onStepPropertyChange}
      placeholder={hotProperty.placeholder}
      smartDetectors={hotProperty.smartDetectors}
      smartDetectorsSuccess={hotProperty.smartDetectorsSuccess}
      multiline={hotProperty.multiline}
      monospace={hotProperty.monospace}
      autoFocus={isAutoFocus}
      updated={existing != null && value !== savedValue}
      inline={hotProperty.inline}
      description={hotProperty.description}
      toggleable={hotProperty.toggleable}
    />
  )
}
